import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, MapPin, Trash2, Gift } from "lucide-react";
import { api, apiErr, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Profile() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "" });
  const [addresses, setAddresses] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  useEffect(() => { if (user === false) nav("/login?next=/profile"); if (user) setForm({ name: user.name || "", phone: user.phone || "" }); }, [user]);
  useEffect(() => { if (user) { api.get("/addresses").then((r) => setAddresses(r.data)); api.get("/loyalty/me").then((r) => setLoyalty(r.data)).catch(() => {}); } }, [user]);
  if (!user) return null;

  const save = async () => { try { await api.put("/auth/profile", form); await refresh(); toast.success("Profile updated"); } catch (e) { toast.error(apiErr(e)); } };
  const delAddr = async (id) => { await api.delete(`/addresses/${id}`); setAddresses((p) => p.filter((a) => a.id !== id)); };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
      <h1 className="text-2xl font-bold">My Profile</h1>
      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <h2 className="font-bold flex items-center gap-2 mb-4"><User size={18} className="text-emerald-600" /> Account Details</h2>
        <div className="space-y-3">
          <div><label className="text-xs font-semibold text-slate-500">Email</label><input disabled value={user.email} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-slate-50 mt-1" /></div>
          <div><label className="text-xs font-semibold text-slate-500">Name</label><input data-testid="profile-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mt-1" /></div>
          <div><label className="text-xs font-semibold text-slate-500">Phone</label><input data-testid="profile-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mt-1" /></div>
          <button data-testid="save-profile" onClick={save} className="bg-emerald-600 text-white rounded-lg px-5 py-2 font-bold text-sm">Save</button>
        </div>
      </div>
      {loyalty?.config?.enabled && (
        <div className="rounded-2xl p-5 text-white bg-gradient-to-br from-emerald-600 to-emerald-700" data-testid="loyalty-wallet">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5"><Gift size={13} /> Reward Points</p>
              <p className="text-3xl font-extrabold mt-1" data-testid="loyalty-balance">{loyalty.balance} pts</p>
              <p className="text-emerald-100 text-sm">Worth {inr(loyalty.wallet_value)} at checkout</p>
            </div>
            <div className="text-right text-xs text-emerald-100">Earn {loyalty.config.points_per_earn} pt / ₹{loyalty.config.earn_per_amount}<br />1 pt = {inr(loyalty.config.redeem_value_per_point)}</div>
          </div>
          {loyalty.ledger?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-emerald-500/40 space-y-1">
              {loyalty.ledger.slice(0, 4).map((l) => (
                <div key={l.id} className="flex justify-between text-xs text-emerald-50"><span>{l.type === "earn" ? "Earned" : "Redeemed"} · {l.order_no}</span><span className="font-bold">{l.points > 0 ? "+" : ""}{l.points} pts</span></div>
              ))}
            </div>
          )}
          <button data-testid="view-points-history" onClick={() => nav("/rewards")} className="mt-3 text-xs font-bold text-white/90 underline underline-offset-2">View full history →</button>
        </div>
      )}
      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <h2 className="font-bold flex items-center gap-2 mb-4"><MapPin size={18} className="text-emerald-600" /> Saved Addresses</h2>
        {addresses.length === 0 ? <p className="text-sm text-slate-400">No addresses. Add one at checkout.</p> : (
          <div className="space-y-2">{addresses.map((a) => (
            <div key={a.id} data-testid={`profile-addr-${a.id}`} className="flex justify-between items-start p-3 rounded-xl bg-slate-50 text-sm">
              <div><span className="font-bold">{a.label}</span> · {a.name} · {a.phone}<p className="text-slate-500">{a.line1}, {a.city}, {a.state} - {a.pincode}</p></div>
              <button onClick={() => delAddr(a.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={15} /></button>
            </div>
          ))}</div>
        )}
      </div>
    </div>
  );
}
